
import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Chip, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, InputAdornment
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpIcon from '@mui/icons-material/Help';
import Tooltip from '@mui/material/Tooltip';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function LocationMarker({ position, setPosition }) {
  const map = useMapEvents({
    click(e) {
      if (setPosition) {
          setPosition(e.latlng);
          map.flyTo(e.latlng, map.getZoom());
      }
    },
  });

  return position ? <Marker position={position} /> : null;
}

function PunchingDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  
  const [filterDate, setFilterDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  
  // Edit State
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({ id: null, check_in_location: '', check_out_location: '' });
  const [updating, setUpdating] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [userLocation, setUserLocation] = useState(null);
  const [editLocOpen, setEditLocOpen] = useState(false);
  const [newUserLocation, setNewUserLocation] = useState({ address: '', lat: 0, lng: 0 });

  // Distance Calculation Helper (Haversine Formula) in Meters
  const getDistanceFromLatLonInMeters = (lat1, lon1, lat2, lon2) => {
    var R = 6371; // Radius of the earth in km
    var dLat = deg2rad(lat2-lat1);  // deg2rad below
    var dLon = deg2rad(lon2-lon1); 
    var a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2)
      ; 
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    var d = R * c; // Distance in km
    return d * 1000; // Meters
  }

  const deg2rad = (deg) => {
    return deg * (Math.PI/180)
  }

  const isLocationMatch = (logLocString) => {
      // 1. Check if user has assigned location
      if (!userLocation || !userLocation.latitude || !userLocation.longitude) return 'unknown'; // Grey
      
      // 2. Parse log location
      if (!logLocString || typeof logLocString !== 'string') return 'unknown';
      const parts = logLocString.split(',');
      if (parts.length !== 2) return 'unknown'; // Likely an address manually typed
      
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      
      if (isNaN(lat) || isNaN(lng)) return 'unknown';
      
      // 3. Calculate Distance
      const dist = getDistanceFromLatLonInMeters(userLocation.latitude, userLocation.longitude, lat, lng);
      
      // 4. Threshold (e.g. 500 meters)
      return dist <= 500 ? 'match' : 'mismatch';
  }

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const [res, locRes] = await Promise.all([
             axios.get(`${API_URL}/punching/attendance/${id}`, { headers }),
             axios.get(`${API_URL}/punching/locations`, { headers }) // We need to filter this manually or need a new endpoint
        ]);
        
        setAttendance(res.data);
        if (res.data.length > 0) {
            setUserName(res.data[0].user_name);
        }

        // Find assigned location for this user
        const assigned = locRes.data.find(l => l.user_id === parseInt(id));
        if (assigned) {
            setUserLocation(assigned);
            setNewUserLocation({ address: assigned.address, lat: assigned.latitude, lng: assigned.longitude });
        }

      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  const handleUpdateLocation = async () => {
      if (!userLocation) return;
      try {
          await axios.put(`${API_URL}/punching/locations/${userLocation.id}`, {
              address: newUserLocation.address,
              latitude: newUserLocation.lat,
              longitude: newUserLocation.lng
          }, { headers });
          
          setUserLocation({ ...userLocation, address: newUserLocation.address, latitude: newUserLocation.lat, longitude: newUserLocation.lng });
          setEditLocOpen(false);
          alert('Assigned location updated');
      } catch (e) {
          alert('Error updating location');
      }
  }

  const handleEditClick = (row) => {
      setEditData({
          id: row.id,
          check_in_location: row.check_in_location || '',
          check_out_location: row.check_out_location || '-'
      });
      setEditOpen(true);
  };

  const handleSaveEdit = async () => {
      setUpdating(true);
      try {
          // Prepare payload - only send if not dash
          const payload = {
              check_in_location: editData.check_in_location
          };
          if (editData.check_out_location !== '-') {
              payload.check_out_location = editData.check_out_location;
          }

          await axios.put(`${API_URL}/punching/attendance/${editData.id}`, payload, { headers });
          
          // Update local state
          setAttendance(prev => prev.map(p => p.id === editData.id ? { 
              ...p, 
              check_in_location: editData.check_in_location,
              check_out_location: editData.check_out_location === '-' ? p.check_out_location : editData.check_out_location 
          } : p));
          
          setEditOpen(false);
      } catch (err) {
          alert('Failed to update location');
      } finally {
          setUpdating(false);
      }
  };

  const handleExport = () => {
    const headers = ["Date", "In Time", "In Location", "Out Time", "Out Location", "Status"];
    const csvContent = [
        headers.join(','),
        ...filteredAttendance.map(row => [
            row.date,
            row.check_in,
            `"${row.check_in_location}"`,
            row.check_out,
            `"${row.check_out_location}"`,
            row.check_out !== '-' ? 'Completed' : 'Active'
        ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Attendance_${userName || 'User'}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) return <Loading message="Loading Attendance Records..." />;

  const filteredAttendance = attendance.filter(row => {
    const dateMatch = filterDate ? row.date === filterDate : true;
    const statusMatch = filterStatus === 'All' ? true :
        filterStatus === 'Active' ? row.check_out === '-' :
        row.check_out !== '-';
    return dateMatch && statusMatch;
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/punching')} color="inherit">
            Back
        </Button>
      </Box>

      {/* Header & Filters */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, mb: 3, gap: 2 }}>
         <Box>
            <Typography variant="h5" fontWeight="bold">Attendance Logs</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" color="textSecondary">{userName || 'User Details'}</Typography>
                {userLocation && (
                    <Chip 
                        label={userLocation.address || 'Location Assigned'} 
                        size="small" 
                        icon={<EditIcon sx={{ fontSize: 14 }} />} 
                        onClick={() => setEditLocOpen(true)}
                        sx={{ cursor: 'pointer' }}
                    />
                )}
            </Box>
         </Box>
         
         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'center' }}>
            {/* Date Filter */}
            <TextField
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                sx={{ bgcolor: 'white', borderRadius: 1, width: { xs: '100%', sm: 'auto' } }}
            />
            
            <Button 
                variant="outlined" 
                startIcon={<DownloadIcon />} 
                onClick={handleExport}
                sx={{ borderRadius: 5, width: { xs: '100%', sm: 'auto' } }}
            >
                Export
            </Button>
         </Box>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0', overflowX: 'auto' }}>
        <Table sx={{ minWidth: 650 }}>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                    <TableCell fontWeight="bold">Date</TableCell>
                    <TableCell fontWeight="bold">In Time</TableCell>
                    <TableCell fontWeight="bold">In Location</TableCell>
                    <TableCell fontWeight="bold">Out Time</TableCell>
                    <TableCell fontWeight="bold">Out Location</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {filteredAttendance.map((row) => (
                    <TableRow key={row.id} hover>
                        <TableCell>{row.date}</TableCell>
                        <TableCell sx={{ color: 'success.main', fontWeight: 'medium' }}>
                            {row.check_in}
                        </TableCell>
                        <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {row.check_in_location}
                                {(() => {
                                    const status = isLocationMatch(row.check_in_location);
                                    if (status === 'match') return <Tooltip title="Matches Assigned Location"><CheckCircleIcon color="success" fontSize="small" /></Tooltip>;
                                    if (status === 'mismatch') return <Tooltip title="Different from Assigned Location"><CancelIcon color="error" fontSize="small" /></Tooltip>;
                                    return <Tooltip title="Cannot verify location (Not coordinates or no assigned location)"><HelpIcon color="disabled" fontSize="small" /></Tooltip>;
                                })()}
                            </Box>
                        </TableCell>
                        <TableCell sx={{ color: 'error.main', fontWeight: 'medium' }}>
                            {row.check_out}
                        </TableCell>
                        <TableCell>
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {row.check_out_location}
                                {(() => {
                                    if (!row.check_out_location || row.check_out_location === '-') return null;
                                    const status = isLocationMatch(row.check_out_location);
                                    if (status === 'match') return <Tooltip title="Matches Assigned Location"><CheckCircleIcon color="success" fontSize="small" /></Tooltip>;
                                    if (status === 'mismatch') return <Tooltip title="Different from Assigned Location"><CancelIcon color="error" fontSize="small" /></Tooltip>;
                                    return <Tooltip title="Cannot verify location"><HelpIcon color="disabled" fontSize="small" /></Tooltip>;
                                })()}
                            </Box>
                        </TableCell>
                    </TableRow>
                ))}
                {filteredAttendance.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                            {attendance.length === 0 ? "No attendance records found." : "No records match your filters."}
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Location</DialogTitle>
          <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <TextField 
                      label="Check In Location" 
                      fullWidth 
                      value={editData.check_in_location}
                      onChange={(e) => setEditData({...editData, check_in_location: e.target.value})}
                  />
                  {editData.check_out_location !== '-' && (
                       <TextField 
                          label="Check Out Location" 
                          fullWidth 
                          value={editData.check_out_location}
                          onChange={(e) => setEditData({...editData, check_out_location: e.target.value})}
                      />
                  )}
              </Box>
          </DialogContent>
          <DialogActions>
              <Button onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveEdit} disabled={updating}>
                  {updating ? 'Saving...' : 'Save Changes'}
              </Button>
          </DialogActions>
      </Dialog>

      {/* Edit Assigned Location Dialog */}
      <Dialog open={editLocOpen} onClose={() => setEditLocOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Update Assigned Location</DialogTitle>
          <DialogContent>
             <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField 
                    label="Address" 
                    fullWidth 
                    value={newUserLocation.address} 
                    onChange={e => setNewUserLocation({...newUserLocation, address: e.target.value})}
                />
                <Box sx={{ height: 400, borderRadius: 2, overflow: 'hidden', border: '1px solid #ddd', width: '100%' }}>
                    <MapContainer center={newUserLocation.lat ? [newUserLocation.lat, newUserLocation.lng] : [25, 45]} zoom={10} style={{ height: '100%', width: '100%' }}>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <LocationMarker 
                            position={newUserLocation.lat ? [newUserLocation.lat, newUserLocation.lng] : null} 
                            setPosition={(pos) => setNewUserLocation(prev => ({ ...prev, lat: pos.lat, lng: pos.lng }))} 
                        />
                    </MapContainer>
                </Box>
                <Typography variant="caption" color="textSecondary">
                    Selected Coordinates: {newUserLocation.lat?.toFixed(6)}, {newUserLocation.lng?.toFixed(6)}
                </Typography>
             </Box>
          </DialogContent>
          <DialogActions>
               <Button onClick={() => setEditLocOpen(false)}>Cancel</Button>
               <Button variant="contained" onClick={handleUpdateLocation}>Update</Button>
          </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PunchingDetails;
